import { Address } from "../Objects/models/Address";

/**
 * Lecture d'une adresse renvoyée par Google (geocodeByPlaceId) par type de composant, jamais par position.
 *
 * Google ne renvoie pas toujours les mêmes niveaux : en outre-mer (Martinique, Guadeloupe, Réunion, Guyane,
 * Mayotte) il n'y a pas de niveau « département » (administrative_area_level_2), parfois pas de ville
 * (locality). Une lecture par position décalait alors tous les champs : le pays recevait le code postal et le
 * code postal restait vide. Même règle de repli que le backend (AddressController) : le département reprend
 * la région, à défaut la ville, et inversement.
 */

export type GoogleAddressComponent = {
  long_name: string;
  short_name?: string;
  types: string[];
};

export type GoogleGeocodeResult = {
  place_id: string;
  formatted_address: string;
  address_components: GoogleAddressComponent[];
  geometry: { location: { lat: () => number; lng: () => number } };
};

export type AddressParts = {
  streetNumber: string;
  streetName: string;
  streetNameShort: string;
  city: string;
  department: string;
  region: string;
  country: string;
  zip: string;
};

const find = (components: GoogleAddressComponent[], ...types: string[]): GoogleAddressComponent | undefined =>
  components.find((component) => types.some((type) => component.types?.includes(type)));

const longName = (components: GoogleAddressComponent[], ...types: string[]): string =>
  find(components, ...types)?.long_name ?? "";

/** Champs d'une adresse à partir des composants Google, avec les replis de l'outre-mer. */
export const addressPartsFromComponents = (components: GoogleAddressComponent[] = []): AddressParts => {
  const route = find(components, "route");
  const city = longName(components, "locality", "postal_town", "sublocality_level_1", "administrative_area_level_3");
  const level2 = longName(components, "administrative_area_level_2");
  const level1 = longName(components, "administrative_area_level_1");

  return {
    streetNumber: longName(components, "street_number"),
    // sans voie identifiée, Google met le lieu en premier composant (même repli que le backend)
    streetName: route?.long_name ?? components[0]?.long_name ?? "",
    streetNameShort: route?.short_name ?? route?.long_name ?? "",
    city,
    department: level2 || level1 || city,
    region: level1 || level2 || city,
    country: longName(components, "country"),
    zip: longName(components, "postal_code"),
  };
};

/** Adresse de l'application construite à partir d'un résultat de geocodeByPlaceId. */
export const addressFromGeocode = (result: GoogleGeocodeResult, owner = "", isDefault = true): Address => {
  const parts = addressPartsFromComponents(result.address_components);
  const address = new Address(
    result.place_id,
    parts.streetNumber,
    parts.streetName,
    parts.city,
    parts.department,
    parts.region,
    parts.country,
    parts.zip,
    result.geometry.location.lat().toString(),
    "",
    result.formatted_address,
    owner,
    isDefault
  );
  address.streetNameShort = parts.streetNameShort;
  address.longitude = result.geometry.location.lng().toString();

  return address;
};
